import { V2 } from "../theme/DesignV2";
import React, { ChangeEvent, JSX, useEffect, useId, useState } from "react";
import { styled, useTheme } from "styled-components";
import { Box, createCSSFromObject, HBox, VBox } from "../box/Box";
import { InfoButton } from "../info/InfoButton";
import { Select } from "../select/Select";
import Spacer from "../spacer/Spacer";
import { Typo } from "../text/Typo";
import { ITheme, RecursiveCSS } from "../theme/GlobalTheme";

export const InputBoxStyle = (disable: boolean, readOnly?: boolean) => {
	return {
		padding: "0px",
		border: disable ? "1px solid #E3E7EF" : "1px solid #DCE1EB",
		cursor: readOnly || disable ? "default" : "text",
		borderRadius: V2.controlRadius,
		backgroundColor: disable ? "#F3F4F6" : "#FFFFFF",
		boxShadow: "none",
		"&:focus-within": { borderColor: "#5B84AD", boxShadow: "0 0 0 3px rgba(30, 58, 95,.10)" },
		transition: "border-color .18s ease, box-shadow .18s ease, background-color .18s ease",
		"&:hover": {
			border: disable ? "1px solid #E5E7EB" : "1px solid #94A3B8",
			backgroundColor: disable ? "#F3F4F6" : "#FFFFFF",
			boxShadow: disable ? "none" : "0 2px 8px rgba(15, 23, 42, 0.06)",
		},
	};
};

export const InputFontStyle = (size: "small" | "medium" | "large") => {
	return {
		outline: "0",
		margin: "0",
		padding: size === "small" ? "8px 12px" : size === "large" ? "15px 18px" : "12px 14px",
		fontSize: size === "small" ? "0.82rem" : size === "large" ? "1.05rem" : "0.94rem",
		lineHeight: "1.35",
		height: size === "small" ? "34px" : size === "large" ? "54px" : "44px",
		boxSizing: "border-box" as const,
		"@media (max-width: 768px)": {
			height: size === "large" ? "54px" : "44px",
			minHeight: "44px",
			fontSize: "16px",
		},
	};
};

export interface IInputProps {
	sx?: RecursiveCSS;
	isCard?: boolean;
	sxInput?: RecursiveCSS;
	name: string;
	label?: string;
	iconRight?: JSX.Element;
	iconLeft?: JSX.Element;
	placeholder?: string;
	autoComplete?: string;
	data: { [key: string]: string | number | boolean };
	onChange?: (value: string) => void;
	onFocusProps?: () => void;
	onBloorProps?: () => void;
	size?: "small" | "medium" | "large";
	subtitle?: string;
	type?: "switchbox" | "text" | "email" | "password" | "phone" | "textarea" | "date" | "search" | "dateInput" | "birthday" | "number";
	switchboxLabels?: { yes: string; no: string };
	inputColor?: string;
	disabled?: boolean;
	readOnly?: boolean;
	multiline?: boolean;
	maxLength?: number;
	description?: string;
	units?: string;
	mandatory?: boolean;
	id?: string;
	refvalue?: string | null;
	validator?: (val: string) => boolean;
	formatAmount?: boolean;
	asPercent?: boolean;
	variant?: "default" | "reference";
	slider?: { min: number; max: number; step?: number; zeroLabel?: string };
}

const TextAreaEl = styled.textarea<{ $size?: string; $color?: string }>`
	width: calc(100%);
	display: block;
	border: none;
	padding: 0;
	outline: 0;
	margin: 0;
	height: auto;
	padding: 12px 16px;
	font-size: 0.87rem;
	box-sizing: border-box;
	background-color: transparent;
	${props => (props.$size ? `font-size:${props.$size};` : ``)};
	${props => (props.$color ? `color:${props.$color};` : ``)};
	resize: none;
	&::placeholder {
		font-style: normal;
		color: #9aa4b2;
	}
	&:focus {
		background-color: transparent;
	}
	&:disabled {
	}
`;

const AmountRange = styled.input`
	appearance: none;
	width: 100%;
	height: 32px;
	margin: 0;
	cursor: pointer;
	background: transparent;
	&::-webkit-slider-runnable-track { height: 5px; background: #e7e6f5; border-radius: 99px; }
	&::-moz-range-track { height: 5px; background: #e7e6f5; border-radius: 99px; }
	&::-moz-range-progress { height: 5px; background: #2b5c8a; border-radius: 99px; }
	&::-webkit-slider-thumb {
		appearance: none; width: 22px; height: 22px; margin-top: -8.5px;
		background: #2b5c8a; border: 3px solid white; border-radius: 50%;
		box-shadow: 0 2px 8px #2b5c8a4d; transition: box-shadow 180ms ease, transform 180ms ease;
	}
	&::-moz-range-thumb { width: 16px; height: 16px; background: #2b5c8a; border: 3px solid white; border-radius: 50%; }
	&:active::-webkit-slider-thumb { transform: scale(1.12); box-shadow: 0 0 0 6px #2b5c8a1a; }
	&:disabled { opacity: .5; cursor: default; }
`;

// 0 0 16 10
//!TODO: replace with icon component
const eyeOpened = (
	<svg width="14" height="10" viewBox="0 0 16 10" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M8 0C1.3 0 0 5.1 0 5.1C0 5.1 2.2 9.2 7.9 9.2C13.6 9.2 16 5.2 16 5.2C16 5.2 14.7 0 8 0ZM5.3 1.5C5.8 1.2 6.6 1.2 6.6 1.2C6.6 1.2 6.1 2.1 6.1 2.8C6.1 3.5 6.3 3.9 6.3 3.9L5.2 4.1C5.2 4.1 4.9 3.6 4.9 2.9C4.9 2.1 5.3 1.5 5.3 1.5ZM7.9 8.2C3.8 8.2 1.7 5.9 1.1 5C1.4 4.3 2.2 2.8 4.2 1.8C4.1 2.2 4 2.6 4 3.1C4 5.3 5.8 7.1 8 7.1C10.2 7.1 12 5.3 12 3.1C12 2.6 11.9 2.2 11.8 1.8C13.8 2.7 14.6 4.3 14.9 5C14.2 5.9 12.1 8.2 7.9 8.2Z"
			fill="#1C2E45"
			fillOpacity="0.6"
		/>
	</svg>
);

//!TODO: replace with icon component
const eyeClosed = (
	<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M16.9 9.19995L16.1 9.99995C17.8 10.9 18.6 12.3 18.9 13C18.2 13.9 16.1 16.1 11.9 16.1C11.2 16.1 10.7 16 10.1 15.9L9.29999 16.7C10.1 17 11 17.1 11.9 17.1C17.6 17.1 20 13.1 20 13.1C20 13.1 19.4 10.7 16.9 9.19995Z"
			fill="#1C2E45"
			fillOpacity="0.6"
		/>
		<path d="M16 11.1C16 10.8 16 10.5 15.9 10.3L11.1 15C11.4 15 11.7 15.1 12 15.1C14.2 15.1 16 13.3 16 11.1Z" fill="#1C2E45" fillOpacity="0.6" />
		<path
			d="M19.3 4L14.9 8.4C14.1 8.2 13.1 8 12 8C5.3 8 4 13.1 4 13.1C4 13.1 5 14.9 7.3 16.1L4 19.3V20H4.7L20 4.7V4H19.3V4ZM8 15.3C6.4 14.6 5.5 13.5 5.1 13C5.4 12.3 6.2 10.8 8.2 9.8C8.1 10.2 8 10.6 8 11.1C8 12.2 8.5 13.3 9.3 14L8 15.3ZM10.2 11.9L9.2 12.1C9.2 12.1 8.9 11.6 8.9 10.9C8.9 10.1 9.3 9.4 9.3 9.4C9.8 9.1 10.6 9.1 10.6 9.1C10.6 9.1 10.1 10 10.1 10.8C10 11.5 10.2 11.9 10.2 11.9Z"
			fill="#1C2E45"
			fillOpacity="0.6"
		/>
	</svg>
);

export const InputEl = styled.input<{
	$color?: string;
	$sx?: React.CSSProperties;
	$readOnly?: boolean;
	$size: "small" | "medium" | "large";
	$variant?: "default" | "reference";
}>`
	width: calc(100%);
	display: block;
	border: none;
	box-sizing: border-box;
	background-color: transparent;

	${props => createCSSFromObject(InputFontStyle(props.$size))}
	${props => (props.$color ? `color:${props.$color};` : ``)};
	${props => createCSSFromObject(props.$sx ?? {})}
	${props => props.$readOnly && `pointer-events: none; cursor: default;`}

	&::placeholder {
		font-style: ${props => (props.$variant === "reference" ? "normal" : "italic")};
		opacity: 1;
		color: ${props => (props.$variant === "reference" ? "#9a918a" : "#9AA4B2")};
		letter-spacing: ${props => (props.$variant === "reference" ? "0.08em" : "inherit")};
		text-transform: ${props => (props.$variant === "reference" ? "uppercase" : "inherit")};
	}
	&:focus {
		/* background-color: transparent; */
		${props => props.$sx?.backgroundColor ?? "background-color: transparent;"}
	}
	&:disabled {
	}
`;

const Input = ({
	multiline,
	disabled,
	readOnly,
	subtitle,
	type,
	label,
	data,
	name,
	iconRight,
	iconLeft,
	placeholder,
	autoComplete,
	onChange,
	sx,
	sxInput,
	size,
	inputColor,
	maxLength,
	isCard,
	onFocusProps,
	onBloorProps,
	description,
	units,
	mandatory,
	id,
	refvalue,
	validator,
	formatAmount,
	switchboxLabels,
	asPercent,
	variant = "default",
	slider,
}: IInputProps) => {
	const theme = useTheme() as ITheme;
	const [value, setValue] = useState<string>("");
	const [_valid, setValid] = useState<boolean>(true);
	const generatedId = useId();
	const inputId = id ? `value_${id}` : generatedId;
	const [decoration, setDecoration] = useState<"valid" | "unvalid" | "neutral" | undefined>();

	if (!size) size = "medium";

	const applyAmountFormat = (val: string): string => {
		const clean = val.replace(/[^\d.]/g, "");
		if (!clean) return "";
		const parts = clean.split(".");
		const intRaw = parts[0].replace(/^0+(\d)/, "$1");
		const intPart = intRaw.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
		if (parts.length > 1) {
			return intPart + "." + parts[1].slice(0, 2);
		}
		return intPart;
	};

	useEffect(() => {
		const raw = data[name] + "";
		if (type === "birthday" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
			const parts = raw.split("-");
			setValue(`${parts[2]}.${parts[1]}.${parts[0]}`);
		} else {
			setValue(formatAmount ? addDecimalPlaces(applyAmountFormat(raw)) : raw);
		}
	}, [data]);

	const addDecimalPlaces = (val: string): string => {
		if (val === "") return "";
		if (val.indexOf(".") === -1) {
			return val + ".00";
		} else {
			const parts = val.split(".");
			if (parts[1].length === 0) {
				return val + "00";
			} else if (parts[1].length === 1) {
				return val + "0";
			}
		}
		return val;
	};

	const [focused, setFocused] = useState<boolean>(false);
	const [showPasswd, setShowPasswd] = useState<boolean>(false);

	const handleOnChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		data[name] = e.target.value;

		if (type === "switchbox") {
			data[name] = e.target.value === "true" ? true : false;
		}

		if (finalType === "date") {
			if (!e.target.value) {
				data[name] = "";
				if (onChange) onChange("");
				setValue("");
				return;
			}

			// create date from yyyy-mm-dd
			const dateParts = e.target.value.split("-");
			const dateObj = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
			if (Number.isNaN(dateObj.getTime())) {
				data[name] = "";
				if (onChange) onChange("");
				setValue("");
				return;
			}

			data[name] = dateObj.getFullYear() + "-" + String(dateObj.getMonth() + 1).padStart(2, "0") + "-" + String(dateObj.getDate()).padStart(2, "0");
			if (onChange) onChange(data[name]);
			setValue(data[name]);
		} else if (finalType === "birthday") {
			const formattedValue = formatBirthdayValue(e.target.value, value);
			if (formattedValue.length === 10) {
				const parts = formattedValue.split(".");
				data[name] = `${parts[2]}-${parts[1]}-${parts[0]}`;
				if (onChange) onChange(data[name]);
			} else {
				data[name] = formattedValue;
				if (onChange) onChange(formattedValue);
			}
			setValue(formattedValue);
		} else if (type === "dateInput") {
			// Форматирование MM/YY для мобильных браузеров
			const formattedValue = formatDateInputValue(e.target.value, value);
			data[name] = formattedValue;
			callValidator(formattedValue);
			if (onChange) onChange(formattedValue);
			setValue(formattedValue);
		} else if (formatAmount) {
			const raw = e.target.value.replace(/\s/g, "").replace(",", ".");
			if (raw !== "" && !/^\d*\.?\d{0,2}$/.test(raw)) return;
			data[name] = raw;
			callValidator(raw);
			if (onChange) onChange(raw);
			setValue(applyAmountFormat(raw));
		} else if (asPercent) {
			let raw = e.target.value.replace(/[^\d.]/g, "");
			if (raw !== "" && !/^\d*\.?\d{0,2}$/.test(raw)) return;
			raw = raw.replace(/^0+(\d)/, "$1");
			const num = parseFloat(raw);
			if (!isNaN(num) && num > 100) return;
			data[name] = raw;
			callValidator(raw);
			if (onChange) onChange(raw);
			setValue(raw);
		} else if (type === "number") {
			// A lone leading zero (the field's default) should be replaced by
			// whatever digit the user types next, not prefixed: "0" + "1" => "1".
			const raw = e.target.value.replace(/^0+(\d)/, "$1");
			data[name] = raw;
			callValidator(raw);
			if (onChange) onChange(raw);
			setValue(raw);
		} else {
			callValidator(e.target.value);
			if (onChange) onChange(e.target.value);
			const filteredValue = isCard
				? e.target.value
						.replace(/[^\dA-Z]/g, "")
						.replace(/(.{4})/g, "$1 ")
						.trim()
				: e.target.value;

			setValue(filteredValue);
		}
	};

	const callValidator = (val: string) => {
		if (refvalue !== undefined && validator) {
			const valid = validator(val);
			if (Number(refvalue) === Number(val)) {
				setDecoration("neutral");
			} else {
				valid ? setDecoration("valid") : setDecoration("unvalid");
			}
			setValid(valid);
		}
	};

	const onFocus = () => {
		onFocusProps && onFocusProps();
		setFocused(true);
	};

	const onBlur = () => {
		onBloorProps && onBloorProps();
		setFocused(false);
	};

	const formatBirthdayValue = (rawValue: string, prevValue: string): string => {
		const isDeleting = rawValue.length < prevValue.length;
		const digits = rawValue.replace(/\D/g, "");
		if (digits.length === 0) return "";

		let day = digits.substring(0, 2);
		let month = digits.substring(2, 4);
		let year = digits.substring(4, 8);

		if (day.length === 2) {
			let d = parseInt(day, 10);
			if (d === 0) day = "01";
			if (d > 31) day = "31";
		}
		if (month.length === 2) {
			let m = parseInt(month, 10);
			if (m === 0) month = "01";
			if (m > 12) month = "12";
		}

		let res = day;
		if (day.length === 2 && !isDeleting && digits.length >= 2) res += ".";
		if (month.length > 0) res += month;
		if (month.length === 2 && !isDeleting && digits.length >= 4) res += ".";
		if (year.length > 0) res += year;

		return res;
	};

	// Форматирование MM/YY через onChange (работает на мобильных браузерах)
	const formatDateInputValue = (rawValue: string, prevValue: string): string => {
		const delimiter = "/";
		const yearMin = new Date().getFullYear() - 2000;
		const yearMax = new Date().getFullYear() - 2000 + 25;

		// Определяем, удаляет ли пользователь символы
		const isDeleting = rawValue.length < prevValue.length;

		// Оставляем только цифры
		const digits = rawValue.replace(/\D/g, "");

		if (digits.length === 0) return "";

		let month = "";
		let year = "";

		// Обработка месяца (первые 1-2 цифры)
		if (digits.length >= 1) {
			const firstDigit = parseInt(digits[0], 10);

			if (digits.length === 1) {
				// Если первая цифра > 1, добавляем ведущий 0 (только при добавлении)
				if (firstDigit > 1 && !isDeleting) {
					month = "0" + digits[0];
				} else {
					month = digits[0];
				}
			} else {
				// Две цифры для месяца
				let monthNum = parseInt(digits.substring(0, 2), 10);

				// Валидация месяца 01-12
				if (monthNum === 0) {
					monthNum = 1;
				} else if (monthNum > 12) {
					monthNum = 12;
				}

				month = monthNum.toString().padStart(2, "0");
			}
		}

		// Обработка года (следующие 1-2 цифры после месяца)
		if (digits.length > 2) {
			const yearDigits = digits.substring(2, 4);

			if (yearDigits.length === 2) {
				let yearNum = parseInt(yearDigits, 10);

				// Валидация года в пределах yearMin-yearMax
				if (yearNum < yearMin) {
					yearNum = yearMin;
				} else if (yearNum > yearMax) {
					yearNum = yearMax;
				}

				year = yearNum.toString().padStart(2, "0");
			} else {
				year = yearDigits;
			}
		}

		// Формируем результат
		if (month.length === 2 && year.length > 0) {
			return month + delimiter + year;
		} else if (month.length === 2 && !isDeleting) {
			// Добавляем разделитель только при вводе, не при удалении
			return month + delimiter;
		}

		return month;
	};

	if (!inputColor) inputColor = theme.colors.textPrimary;

	let switchbox_no = "No";
	let switchbox_yes = "Yes";

	if (switchboxLabels) {
		switchbox_no = switchboxLabels.no;
		switchbox_yes = switchboxLabels.yes;
	}

	if (!iconRight && type === "password")
		iconRight = (
			<Box
				element="button"
				type="button"
				tabIndex={-1}
				aria-label={showPasswd ? "Hide password" : "Show password"}
				title={showPasswd ? "Hide password" : "Show password"}
				onClick={() => {
					setShowPasswd(!showPasswd);
				}}
				sx={{
					padding: "0",
					margin: "0",
					border: "0",
					backgroundColor: "transparent",
					cursor: "pointer",
					width: "44px",
					minWidth: "44px",
					height: "44px",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					borderRadius: "9px",
					"&:hover": { backgroundColor: "#F1F5F9" },
					"&:focus-visible": { outline: "3px solid rgba(37, 99, 235, 0.18)", outlineOffset: "-3px" },
				}}>
				{!showPasswd ? eyeOpened : eyeClosed}
			</Box>
		);

	let inputElement = null;
	if (multiline || type === "textarea")
		inputElement = (
			<TextAreaEl
				id={inputId}
				name={name}
				autoComplete={autoComplete}
				maxLength={maxLength ? maxLength : undefined}
				disabled={disabled}
				readOnly={readOnly}
				$color={inputColor}
				onFocus={onFocus}
				onBlur={onBlur}
				placeholder={placeholder}
				$size={size}
				onChange={handleOnChange}
				value={value}
			/>
		);

	if (type === "switchbox") {
		inputElement = (
			<Box
				onClick={() => {
					handleOnChange({
						target: {
							value: value === "true" ? "false" : "true",
						},
					} as any);
				}}
				sx={{
					width: "100%",
					minWidth: "50px",
					position: "relative",
					cursor: disabled ? "default" : "pointer",
					display: "grid",
					gridTemplateColumns: "1fr 1fr",
					alignItems: "center",
					...InputFontStyle(size),
					paddingLeft: "0px",
					paddingRight: "0px",
					pointerEvents: disabled ? "none" : "auto",
					"&>div": {
						paddingLeft: "10px",
						paddingRight: "10px",
						fontSize: "0.86em",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						whiteSpace: "nowrap",
						overflow: "hidden",
						textOverflow: "ellipsis",
					},
					"&>div:nth-child(1)": {
						color: value === "false" ? "#af0c0c" : "#a49c9c",
					},
					"&>div:nth-child(2)": {
						color: value === "false" ? "#a49c9c" : "#1c3a72",
					},
				}}>
				<div>{switchbox_no}</div>
				<div>{switchbox_yes}</div>
				<Box
					sx={{
						position: "absolute",
						width: "calc(50% - 10px)",
						backgroundColor: "#193650",
						height: "2px",
						bottom: "3px",
						left: "5px",
						transition: "transform .2s",
						transform: value === "true" ? "translateX(calc(100% + 10px))" : "translateX(0)",
					}}></Box>
			</Box>
		);
	}

	let finalType = type;
	if (type === "password" && showPasswd) finalType = "text";
	// Money fields hint their shape with a placeholder instead of carrying a literal
	// "0"/"0.00" value the user has to clear before typing.
	const finalPlaceholder = placeholder ?? (formatAmount || type === "number" ? "0.00" : undefined);
	// Native <input type="number"> renders its decimal separator per OS/browser
	// locale (e.g. "19,50" under ru-RU) even though .value stays period-based —
	// text + inputMode="decimal" shows exactly what we store, on every field.
	const htmlInputType = finalType === "number" ? "text" : finalType;
	const htmlInputMode = finalType === "number" ? "decimal" : undefined;

	let dateValue = "";
	if (type === "date") {
		if ((value as any) instanceof Date) {
			dateValue =
				(value as any).getFullYear() +
				"-" +
				String((value as any).getMonth() + 1).padStart(2, "0") +
				"-" +
				String((value as any).getDate()).padStart(2, "0");
		} else {
			// check to date
			try {
				let val: any;
				if (value === parseInt(value as any) + "") {
					val = new Date(parseInt(value as any));
				} else {
					val = new Date(value);
				}

				val = val.getFullYear() + "-" + String(val.getMonth() + 1).padStart(2, "0") + "-" + String(val.getDate()).padStart(2, "0");
				dateValue = val;
			} catch (e) {}
		}
	}

	if (!inputElement)
		inputElement = (
			<InputEl
				id={inputId}
				maxLength={maxLength ? maxLength : undefined}
				disabled={disabled}
				$readOnly={readOnly}
				$sx={sxInput}
				$color={inputColor}
				$variant={variant}
				type={htmlInputType}
				inputMode={htmlInputMode}
				onFocus={onFocus}
				onBlur={onBlur}
				name={name}
				autoComplete={autoComplete}
				placeholder={finalPlaceholder}
				$size={size}
				onChange={handleOnChange}
				value={finalType === "date" ? dateValue : value}
			/>
		);

	let predefinedSelector = null;

	if (type === "phone") {
		predefinedSelector = (
			<Select
				values={[
					{ name: "Mobile", value: "mobile" },
					{ name: "Mobile", value: "mobile" },
					{ name: "Mobile", value: "mobile" },
					{ name: "Mobile", value: "mobile" },
				]}
			/>
		);
	}

	const getBackColor = (val: "valid" | "unvalid" | "neutral" | undefined) => {
		switch (val) {
			case "unvalid":
				return "#FFF5F5";

			case "valid":
				return "#F0FDF4";

			default:
				return "#FFFFFF";
			// return "unset";
		}
	};

	const inputFieldContent = (
		<Box
			data-input-control
			data-input-validation={decoration}
			id={id ? "control_" + id : undefined}
			sx={{
				// Password visibility is a pointer affordance, not the next form step.
				// Keep the control at the same fixed height even when a neighboring
				// field has helper text and makes the surrounding grid row taller.
				flexGrow: type === "password" ? 0 : 1,
				height: type === "password" ? InputFontStyle(size).height : undefined,
				minHeight: type === "password" ? InputFontStyle(size).height : undefined,
				"@media (max-width: 768px)": {
					minHeight: type === "password" ? "44px" : undefined,
					height: type === "password" ? (size === "large" ? "54px" : "44px") : undefined,
				},
				width: "100%",
				boxSizing: "border-box",
				overflow: "hidden",
				display: "flex",

				...InputBoxStyle(disabled ?? false, readOnly ?? false),
				backgroundColor: getBackColor(decoration),

		"&:hover": {
			//backgroundColor:"#e6e3dd", //F0EEEA
			border: disabled || readOnly ? undefined : "1px solid #94A3B8",
		},

				"@media (prefers-color-scheme: dark)": {
					//backgroundColor:"#29272f",
				},

				...(focused ? { border: "1px solid #2563EB", boxShadow: "0 0 0 4px rgba(37, 99, 235, 0.12)" } : {}),
				...(variant === "reference"
					? {
							height: "30px",
							minHeight: "30px",
							border: focused ? "1px solid #bfaea3" : "1px solid #d8cfc8",
							borderRadius: "8px",
							backgroundColor: "#fbfaf8",
							boxShadow: "inset 0 1px 0 rgba(255,255,255,.75)",
							"&:hover": {
								border: "1px solid #bfaea3",
								backgroundColor: "#fff",
							},
						  }
					: {}),
			}}>
			{iconLeft && <Box sx={{ display: "flex", alignItems: "center", alignSelf: "stretch", paddingLeft: "12px", marginRight: "-4px", flexShrink: 0, pointerEvents: "none" }}>{iconLeft}</Box>}
			{inputElement}
			{units && (
				<>
					<VBox
						sx={{
							alignItems: "center",
							justifyContent: "center",
						}}>
						<Typo variant="caption">{units}</Typo>
					</VBox>
					<Spacer size="10px" />
				</>
			)}
			{iconRight}
			{refvalue !== undefined && (
				<Box
					sx={{
						width: "35%",
						flexShrink: "0",
						alignItems: "center",
						justifyContent: "center",
						backgroundColor: "#c9c9c9",
						display: "flex",
						padding: InputFontStyle(size).padding,
					}}>
					<HBox sx={{ alignItems: "baseline" }} gap="4px">
						<Typo
							sx={{
								fontSize: InputFontStyle(size).fontSize,
								color: "#000000",
							}}
							variant="caption">
							{refvalue}
						</Typo>
						<Typo variant="caption" color="#000000">
							{units}
						</Typo>
					</HBox>
				</Box>
			)}
		</Box>
	);

	return (
		<Box
			// id={id ? "container_" + id : undefined}
			sx={{
				display: "flex",
				position: "relative",
				flexDirection: "column",
				gap: "8px",
				...sx,
			}}>
			<HBox sx={{ alignItems: "center", gap: "10px" }}>
				{label && (
					<Typo
						variant="caption"
						sx={{
							color: "#536173",
							fontSize: "0.76rem",
							fontWeight: 700,
							lineHeight: 1.2,
							letterSpacing: 0,
						}}>
						<label htmlFor={inputId}>{label}{mandatory !== undefined && mandatory === true ? " *" : ""}</label>
					</Typo>
				)}
				{description && <InfoButton text={description} />}
			</HBox>

			{predefinedSelector ? (
				<HBox gap="5px">
					{predefinedSelector}
					{inputFieldContent}
				</HBox>
			) : (
				inputFieldContent
			)}

			{slider && (
				<Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
					<AmountRange
						type="range"
						aria-label={`${label || name} slider`}
						aria-valuetext={`${Number(data[name]) === 0 && slider.zeroLabel ? slider.zeroLabel : data[name]}${units || ""}`}
						min={slider.min}
						max={slider.max}
						step={slider.step ?? 1}
						value={Math.max(slider.min, Math.min(slider.max, Number(String(value).replace(/\s/g, "")) || 0))}
						disabled={disabled || readOnly}
						onChange={e => {
							const raw = e.target.value;
							data[name] = raw;
							setValue(formatAmount ? applyAmountFormat(raw) : raw);
							callValidator(raw);
							onChange?.(raw);
						}}
					/>
					<HBox sx={{ justifyContent: "space-between", fontSize: "11px", color: "#617087" }}>
						<span>{slider.zeroLabel || `${slider.min}${units || ""}`}</span>
						<span>{slider.max.toLocaleString()}{units}</span>
					</HBox>
				</Box>
			)}
			{subtitle && (
				<Typo variant="small" sx={{ color: "#64748B", opacity: 1 }}>
					{subtitle}
				</Typo>
			)}
		</Box>
	);
};

export default Input;
